import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CONFIG } from '../config/variables';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiX, FiMonitor, FiLoader, FiAlertCircle, FiExternalLink } = FiIcons;

const DemoPreviewModal = ({ isOpen, onClose, demoUrl, demoTitle }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      setLoading(true);
      setError(false);
    } else {
      document.body.style.overflow = 'unset';
    }

    const handleEsc = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 backdrop-blur-md p-0 md:p-10"
        role="dialog"
        aria-modal="true"
        aria-label="Page preview opened"
      >
        <div className="relative w-full h-full flex flex-col bg-slate-900 border border-slate-700/50 md:rounded-3xl overflow-hidden shadow-2xl">
          {/* Top Bar */}
          <div className="flex items-center justify-between px-6 py-4 bg-black/50 border-b border-slate-700/50">
            <div className="flex items-center gap-3">
              <SafeIcon icon={FiMonitor} className="text-blue-400" />
              <div>
                <h3 className="text-white font-bold text-sm">Page Preview</h3>
                <p className="text-slate-500 text-[10px] hidden sm:block">Example page built using BPA Trade Pages framework</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {demoUrl && demoUrl !== '#' && (
                <a
                  href={demoUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hidden sm:flex items-center gap-1.5 text-blue-400 hover:text-blue-300 text-xs font-semibold px-3 py-1.5 rounded-lg bg-blue-500/10 border border-blue-500/30 transition-colors"
                >
                  Open Page
                  <SafeIcon icon={FiExternalLink} className="w-3 h-3" />
                </a>
              )}
              <button
                onClick={onClose}
                className="p-2 hover:bg-white/10 rounded-full transition-colors text-slate-400 hover:text-white"
              >
                <SafeIcon icon={FiX} className="w-6 h-6" />
              </button>
            </div>
          </div>

          {/* Iframe Container */}
          <div className="flex-grow relative bg-white">
            {loading && (
              <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-slate-900">
                <SafeIcon icon={FiLoader} className="w-10 h-10 text-blue-500 animate-spin mb-4" />
                <p className="text-slate-400 font-medium">Loading page...</p>
              </div>
            )}
            {error ? (
              <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-slate-900 p-6 text-center">
                <SafeIcon icon={FiAlertCircle} className="w-12 h-12 text-red-400 mb-4" />
                <h4 className="text-white font-bold text-lg mb-2">Page unavailable</h4>
                <p className="text-slate-400 max-w-xs">The page server might be temporarily down. Please try again or contact support.</p>
                <button
                  onClick={onClose}
                  className="mt-6 text-blue-400 font-bold underline"
                >
                  Close Preview
                </button>
              </div>
            ) : (
              <iframe
                src={demoUrl && demoUrl !== '#' ? demoUrl : 'about:blank'}
                title={demoTitle}
                className="w-full h-full border-none"
                onLoad={() => setLoading(false)}
                onError={() => setError(true)}
              />
            )}

            {/* Preview Mode Overlay */}
            {CONFIG.PREVIEW_MODE && (
              <div className="absolute bottom-4 left-4 z-20 bg-yellow-500/90 text-black px-4 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest shadow-xl">
                Preview Mode – Example pages are illustrative only
              </div>
            )}
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default DemoPreviewModal;